import React, { useEffect, useState } from 'react';
import {getAccountBalances, getAccountTransactions, transactionsLimit} from "../API/Ledger";
import {
    Container,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Chip, InputAdornment
} from "@mui/material";
import {findRefundedTransactions, formatDateTime, formatMoney, formatMoneyTextColor} from "../utils";
import {colorEasyRed, colorSecondary, colorText} from "../App/theme";
import {
    getFullTokenNameByResourceId,
    getTokenNameByResourceId,
    isMobile,
    RESOURCE
} from "../App/utils";
import { IconButton, Tooltip, Button, TextField} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import {createStripeCheckout, refund} from "../API/Stripe";
import LocalLoader from "../Loaders/LocalLoader";
import {TransactionActionsMenu} from "./TransactionActionsMenu";
import AnimatedNumber from "./AnimatedNumber";
import LinearProgress from "@mui/material/LinearProgress";

import {ChatModels, VisualModels, AudioModels, MyServices} from "../constants";
import {GAEvent} from "../App/GA";
import ReactGA from "react-ga4";

let serviceAccounts = {}
Object.entries(MyServices).forEach(([key, data]) => {
    if (data?.accountId) serviceAccounts[data?.accountId] = data?.name
})


export const UserWallets = ({ userProfile }) => {
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [loadingBalances, setLoadingBalances] = useState(false);
    const [loadingBlocking, setLoadingBlocking] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [balances, setBalances] = useState([]);
    const [depositAmountUSD, setDepositAmountUSD] = useState('10');
    const [isAmountValid, setIsAmountValid] = useState(true);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [hasMoreTransactions, setHasMoreTransactions] = useState(true); // Initially assume there are more transactions
    // const [expand, setExpand] = useState(false)
    const [resources, setResources] = useState([]);

    const handleDepositChange = (event) => {
        let value = event.target.value;
        if (parseInt(value) > 100) value = '100';
        if (parseInt(value) < 10) {
            setIsAmountValid(false);
        } else {
            setIsAmountValid(true);
        }
        setDepositAmountUSD(value);
    };

    const handleBuyClick = async () => {
        try {
            setLoadingBlocking(true)

            GAEvent({
                category: "ecommerce",
                action: "buy_button_click",
                value: parseInt(depositAmountUSD)
            });

            const session = await createStripeCheckout(depositAmountUSD);

            if (session?.url) {
                // Redirect to Stripe Checkout
                window.location.href = session.url;
            }

        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoadingBlocking(false);
        }
    };

    const transactionMap = (transaction) => {
        const params = {
            id: transaction.transactionId,
            date: formatDateTime(transaction.transactionId.split('-')[0]),
            reason: transaction.message,
            remoteAccount: transaction.remoteAccount,
            // toAccount: transaction.accountId,
            accountId: transaction.accountId,
            amount: transaction.amount,
            resourceId: transaction.resourceId,
            // paymentIntentId: transaction.remoteAccount === 'gateway' ? transaction.subjectId : undefined,
        }

        if (transaction.subjectId) params.subjectId = transaction.subjectId;
        if (transaction.subject) params.subject = transaction.subject;

        return params;
    }

    const isNew = (t) => (+new Date() - parseInt(t.id.split('-')[0])) < 1000*30;

    const fetchBalancesAndTransactions = ({withLoaders = true}) => {
        if (withLoaders) {
            setLoadingBalances(true);
            setLoadingTransactions(true);
        }

        if (userProfile?.accountId) {
            getAccountBalances(userProfile.accountId, userProfile.walletPrivateKey, userProfile.walletPublicKey)
                .then((balances) => {
                    setBalances(balances);
                    if (withLoaders) setLoadingBalances(false);
                });

            getAccountTransactions(userProfile.accountId, userProfile.walletPrivateKey, userProfile.walletPublicKey)
                .then((fetchedTransactions) => {
                    const formattedTransactions = fetchedTransactions.transactions.map(t => ({
                        ...transactionMap(t),
                    }));
                    setTransactions(formattedTransactions);
                    if (withLoaders) setLoadingTransactions(false);
                });
        }
    }

    useEffect(() => {
        const handleWebsocketMessage = (event) => {
            const data = JSON.parse(event.detail);
            if (data?.tableName === "ledger-account") {
                fetchBalancesAndTransactions({withLoaders: false});
            }
        };

        window.addEventListener('onWebsocketMessage', handleWebsocketMessage);

        return () => {
            window.removeEventListener('onWebsocketMessage', handleWebsocketMessage);
        };
    }, [userProfile]);

    useEffect(() => {
        fetchBalancesAndTransactions({withLoaders: true});
    }, [userProfile]);

    useEffect(() => {
        const AIResources = [
            {
                items: RESOURCE.tokens,
                suffix: RESOURCE.tokens,
                itemPrice: 1,
                model: 'Credits',
                label: `Credits to buy`,
            },
            ...Object.entries(ChatModels).slice(0,1).map(([modeId, data]) => {
                return {
                    items: 'tokens',
                    itemPrice: data?.outputPrice/1000,
                    model: data?.shortName || data?.name,
                    label: 'Tokens to generate'
                }
            }),
            ...Object.entries(VisualModels).slice(0,1).map(([modeId, data]) => {
                return {
                    items: 'images',
                    itemPrice: data?.pricePerRequest,
                    model: data?.shortName || data?.name,
                    label: 'Images to generate'
                }
            }),
            ...Object.entries(AudioModels).slice(0,1).map(([modeId, data]) => {
                return {
                    items: 'minutes',
                    itemPrice: data?.pricePerMinute,
                    model: data?.shortName || data?.name,
                    label: 'Audio minutes to convert to text'
                }
            })
        ];

        const calculateResources = () => {
            const tokens = depositAmountUSD * 100000;

            return AIResources.map(resource => ({
                ...resource,
                quantity: Math.round(tokens / resource.itemPrice),
            }));
        };

        setResources(calculateResources())
    }, [depositAmountUSD]);

    const maxTokens = 100000;


    // New function to handle loading more transactions
    const loadMoreTransactions = async () => {
        if (!userProfile?.accountId || !hasMoreTransactions) return;

        setLoadingBlocking(true);
        try {
            const data = await getAccountTransactions(userProfile.accountId, userProfile.walletPrivateKey, userProfile.walletPublicKey, lastEvaluatedKey);
            const newTransactions = data.transactions.map(transactionMap);
            setTransactions(prevTransactions => [...prevTransactions, ...newTransactions]);
            setLoadingBlocking(false);
            if (data.lastEvaluatedKey) {
                setLastEvaluatedKey(data.lastEvaluatedKey);
            } else {
                // No more items to fetch
                setHasMoreTransactions(false);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setLoadingBlocking(false);
        }
    };

    const handleRefund = async (paymentIntentId, reason, customerReason) => {
        try {
            setLoadingBlocking(true); // Show loading indicator
            const response = await refund(paymentIntentId, reason, customerReason);
        } catch (error) {
            console.error('Refund error:', error);
        } finally {
            setLoadingBlocking(false); // Hide loading indicator
        }
    };

    const mobileStyle = { marginLeft: '-5%', width: '110%'};
    const balance = balances?.find(o => o?.resourceId === RESOURCE.tokens)?.balance;

    // Calculate tokens and images based on the amount in dollars
    let balancesWithBonusAccount = balances;


    const userAccount = balancesWithBonusAccount?.find(o => o.resourceId === RESOURCE.tokens)

    if (userAccount && !balancesWithBonusAccount.find(o => o.resourceId === RESOURCE.bonusTokens)) {
        balancesWithBonusAccount = [
            ...balances,
            {
                // accountName: <span>Earn Bonus Tokens</span>,
                accountName: '',
                balance: 0,
                accountId: userAccount.accountId,
                resourceId: RESOURCE.bonusTokens,
                userId: userAccount.userId
            }
        ]
    }

    const fontSize = (isMobile ? 14 : 16);

    let namedAccounts = {};

    balancesWithBonusAccount.forEach(a => {
        if (a.accountId && a.accountName) {
            namedAccounts[a.accountId] = a.accountName;
        }
    })

    const refunded = transactions?.length ? findRefundedTransactions(transactions) : {};

    return (
        <Container style={isMobile ? mobileStyle: {}}>
            <LocalLoader open={loadingBlocking} blocking={true} />
            {loadingBalances && <Typography variant="h5" sx={{ fontWeight: 'bold' }} style={{ margin: '80px 0px 40px', color: colorText }}>Accounts</Typography>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {loadingBalances ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : balancesWithBonusAccount
                    .filter(o => [RESOURCE.tokens, RESOURCE.bonusTokens].includes(o.resourceId))
                    .sort((a, b) => b.resourceId.localeCompare(a.resourceId))
                    .map((balance) => {

                    return (
                        <Paper key={balance.accountId + balance.resourceId} sx={{ width: isMobile ? '100%' : '46.5%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <Typography variant={isMobile ? 'h8' : 'h5'} component="p" sx={{ color: colorSecondary, fontWeight: 'bold', flexGrow: 1, textAlign: 'center', mx: 2 }}>
                                    <AnimatedNumber number={balance.balance} formatter={formatMoney} />&nbsp;
                                    <span style={{color: formatMoneyTextColor(balance.balance)}}>{getTokenNameByResourceId(balance.resourceId)}</span>
                                    <Typography variant="body1" component="span" sx={{ display: 'block', color: colorText }}>
                                        {getFullTokenNameByResourceId(balance.resourceId)}
                                    </Typography>
                                </Typography>
                                <Box sx={{ flexGrow: 1, textAlign: 'center', mx: 2 }}>

                                    <Typography variant={isMobile ? 'h8' : 'h5'} component="p" sx={{ fontWeight: 'bold' }}>
                                        {balance.accountName}
                                    </Typography>
                                    {!isMobile && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {balance?.accountName && <Typography variant="caption" display="block"
                                                    style={{
                                                        fontSize: isMobile ? 8 : undefined,
                                                        marginTop: isMobile ? '-8px' : '6px'
                                                    }}>
                                            <strong style={{ visibility: isMobile ? 'hidden' : undefined }}>accountId:</strong> {balance.accountId}
                                        </Typography>}
                                        {balance?.accountName && <Tooltip title="Copy Account ID">
                                            <IconButton
                                                onClick={() => navigator.clipboard.writeText(balance.accountId)}
                                                size="small"
                                                style={{ marginTop: '4px'}}
                                                sx={{ ml: 0 }}
                                            >
                                                <ContentCopyIcon style={{fontSize: isMobile ? 10 : 12}} />
                                            </IconButton>
                                        </Tooltip>}
                                    </Box>}
                                </Box>
                            </Box>
                        </Paper>
                     )
                })}

                {!loadingBalances && (
                    <Paper
                        key={'pay'}
                        sx={{ width: isMobile ? '100%' : '46.5%', height: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}
                    >
                        <Box sx={{ width: (isMobile ? '100%' : '95%'), display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TextField
                                label={isAmountValid ? `Buy ${getFullTokenNameByResourceId(RESOURCE.tokens)}` : "Minimum deposit amount is $10"}

                                value={depositAmountUSD}
                                onChange={handleDepositChange}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={{
                                    marginRight: 2, // Add some space between the TextField and the Button
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: isAmountValid ? 'inherit' : colorEasyRed,
                                        },
                                    },
                                }}
                            />
                            <Button onClick={handleBuyClick} disabled={!isAmountValid} variant="contained" color={'success'} sx={{ height: '40px', width: '100px' }}>
                                Buy
                            </Button>
                        </Box>
                        <Box sx={{ width: (isMobile ? '100%' : '95%') }}> {/* This ensures the container takes full width */}
                            {resources.map((resource, index) => (
                                <Box key={index} sx={{ position: 'relative', mb: 1.5 }}>
                                    <Chip label={resource.model} color="primary" size="small" sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1, width: '100px' }} />
                                    <Box sx={{ pl: '110px' }}>
                                        <Typography variant="subtitle1" sx={{ mt: 1, fontSize: 16 }}>{resource.label}</Typography>
                                        <Tooltip title={`${resource.quantity.toLocaleString()} ${resource.items}`}>
                                            <LinearProgress
                                                variant="determinate" value={(resource.quantity / maxTokens) * resource.itemPrice}   />
                                        </Tooltip>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: 16 }}>~ {resource.quantity.toLocaleString()} {resource.suffix || resource.items}</Typography>
                                    </Box>
                                </Box>
                            ))}

                        </Box>
                    </Paper>
                )}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }} style={{ margin: '80px 0px 40px', color: colorText }}>Transactions</Typography>

            {loadingTransactions && !transactions?.length ? (
                <CircularProgress />
            ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {transactions.map((tx, index) => (
                        <Card key={tx.id} className={isNew(tx) ? 'new-item-animation' : ''}>
                            <CardContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    {/* This box will position the TransactionActionsMenu at the top right */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 1 }}>
                                        <TransactionActionsMenu balance={balance} transaction={tx} onRefund={handleRefund} refunded={refunded} />
                                    </Box>
                                    {/* This box will contain all the transaction details */}
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Date:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{tx.date}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Reason:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{tx.reason?.split('\n\n')[0]}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>My Account:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{ namedAccounts[tx.accountId] || tx.accountId.substring(0,8) + '...'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Counterparty:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{serviceAccounts?.[tx.remoteAccount] || tx.remoteAccount}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Amount:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right', color: colorSecondary }}>{formatMoney(tx.amount)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Resource:</Typography>
                                            <Typography variant="body2" sx={{ textAlign: 'right'}}>{tx.resourceId}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : (
                <Paper>
                    <Box sx={{ overflow: 'auto' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: '20%' }}>Date</TableCell>
                                    <TableCell align="left" style={{ width: '30%' }}>Reason</TableCell>
                                    <TableCell align="left" style={{ width: '20%' }}>My Account</TableCell>
                                    <TableCell align="left" style={{ width: '20%' }}>Counterparty</TableCell>
                                    <TableCell align="left" style={{ width: '10%' }}>Amount</TableCell>
                                    <TableCell align="left" style={{ width: '5%' }}>Resource</TableCell>
                                    <TableCell align="left" style={{ width: '10%' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.map((tx) => (
                                    <TableRow
                                        className={isNew(tx) ? 'new-item-animation' : ''}
                                        key={`${tx.id}`}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {tx.date}
                                        </TableCell>
                                        <TableCell align="left">{tx.reason?.split('\n\n')[0]}</TableCell>
                                        <TableCell align="left">{namedAccounts[tx.accountId] || tx.accountId}</TableCell>
                                        <TableCell align="left">{serviceAccounts?.[tx.remoteAccount] || tx.remoteAccount}</TableCell>
                                        <TableCell align="left" style={{color: colorSecondary}}>{formatMoney(tx.amount)}</TableCell>
                                        <TableCell align="left">{tx.resourceId}</TableCell>
                                        <TableCell align="left">
                                            {tx.id && (
                                                <TransactionActionsMenu balance={balance} transaction={tx} onRefund={handleRefund} refunded={refunded} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            )}

            {!loadingTransactions && hasMoreTransactions && transactions?.length >= transactionsLimit && (
                <Box style={{marginTop: '20px'}}>
                    <Button onClick={loadMoreTransactions} variant="contained" size="small" color="primary">
                        Load More
                    </Button>
                </Box>
            )}

        </Container>
    );
};